/**
 * Shared eight-state render contract (design-system §"State Requirements" —
 * loading / empty / normal / stale / overflow / redacted / forbidden / error).
 *
 * Every V3 screen built from S13 onward routes its body through ONE component so
 * the eight states always look and read the same, and so QA can inspect behavior
 * without hidden assumptions:
 *
 *   • loading   — layout-preserving Skeleton bars (never a spinner for content).
 *   • empty     — "no authorized records match" + the next safe action.
 *   • normal    — the populated workspace (the children render).
 *   • stale     — a watermark banner + Refresh; the business state stays visible.
 *   • overflow  — a count of hidden/collapsed records; layout stays stable.
 *   • redacted  — omitted fields / sealed boundaries made explicit, non-leaky.
 *   • forbidden — a disallowed action downgraded with the policy reason + path.
 *   • error     — an inline, recoverable failure traceable to its source.
 *
 * This wraps the canonical `ui/states` trio (LoadingState/EmptyState/ErrorState)
 * for the three states that already had a house style, and adds shadcn-based
 * `Alert`/`Badge` banners for the four projection states (stale / overflow /
 * redacted / forbidden). The four banner markers are also exported on their own
 * so a `normal` screen can compose them inline (e.g. a stale watermark above a
 * still-rendered table, an overflow chip on a section header).
 *
 * Lens/projection doctrine: this is a READ projection of authorized data. It
 * renders state; it never grants policy, mints credentials, or mutates truth.
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  type EmptyStateProps,
} from "@/components/ui/states";

/** The eight canonical states every V3 screen must be able to render. */
export type RenderStateKind =
  | "loading"
  | "empty"
  | "normal"
  | "stale"
  | "overflow"
  | "redacted"
  | "forbidden"
  | "error";

/** All eight kinds, in canonical order — handy for tests and state pickers. */
export const RENDER_STATES: readonly RenderStateKind[] = [
  "loading",
  "empty",
  "normal",
  "stale",
  "overflow",
  "redacted",
  "forbidden",
  "error",
];

export interface RenderStateProps {
  /** Which of the eight states to render. */
  state: RenderStateKind;
  /** The populated workspace — rendered for `normal`, and behind the banners. */
  children?: React.ReactNode;

  /* ----- loading ----- */
  /** Skeleton row count for the `loading` state (default 5). */
  loadingRows?: number;

  /* ----- empty ----- */
  /** Empty-state copy + optional action (forwarded to the shared EmptyState). */
  empty?: EmptyStateProps;

  /* ----- error ----- */
  /** Error to surface in the `error` state (message wins over `error.message`). */
  error?: unknown;
  /** Override the error body message. */
  errorMessage?: string;
  /** Retry handler for `error`/`stale` (renders a calm outline button). */
  onRetry?: () => void;

  /* ----- stale ----- */
  /** ISO/string timestamp the data was generated at, shown in the watermark. */
  staleAsOf?: string;
  /** Override the stale banner body. */
  staleMessage?: string;

  /* ----- overflow ----- */
  /** How many records are hidden/collapsed past the rendered set. */
  overflowCount?: number;
  /** Override the overflow banner body. */
  overflowMessage?: string;

  /* ----- redacted ----- */
  /** Names/labels of fields omitted by policy (shown as a non-leaky list). */
  redactedFields?: readonly string[];
  /** Count of redactions when the names themselves are withheld. */
  redactedCount?: number;
  /** Override the redacted banner body. */
  redactedMessage?: string;

  /* ----- forbidden ----- */
  /** The policy reason a disallowed action was downgraded. */
  forbiddenReason?: string;
  /** The approval/escalation path the viewer can take instead. */
  forbiddenApprovalPath?: string;

  className?: string;
}

/**
 * Render exactly one of the eight states. For the four projection banners
 * (stale / overflow / redacted) the `children` workspace still renders *under*
 * the banner — the doctrine is that a stale or partly-redacted screen must keep
 * showing the underlying business state, never hide it. `forbidden` shows only
 * the downgrade notice (the action is unavailable, not merely watermarked).
 */
export function RenderState({
  state,
  children,
  loadingRows,
  empty,
  error,
  errorMessage,
  onRetry,
  staleAsOf,
  staleMessage,
  overflowCount,
  overflowMessage,
  redactedFields,
  redactedCount,
  redactedMessage,
  forbiddenReason,
  forbiddenApprovalPath,
  className,
}: RenderStateProps) {
  switch (state) {
    case "loading":
      return <LoadingState rows={loadingRows} className={className} />;

    case "empty":
      return <EmptyState {...empty} className={cn(empty?.className, className)} />;

    case "error":
      return (
        <ErrorState
          error={error}
          message={errorMessage}
          onRetry={onRetry}
          className={className}
        />
      );

    case "forbidden":
      return (
        <ForbiddenNotice
          reason={forbiddenReason}
          approvalPath={forbiddenApprovalPath}
          className={className}
        />
      );

    case "stale":
      return (
        <div className={cn("flex flex-col gap-4", className)}>
          <StaleBanner asOf={staleAsOf} message={staleMessage} onRefresh={onRetry} />
          {children}
        </div>
      );

    case "overflow":
      return (
        <div className={cn("flex flex-col gap-4", className)}>
          <OverflowBanner count={overflowCount} message={overflowMessage} />
          {children}
        </div>
      );

    case "redacted":
      return (
        <div className={cn("flex flex-col gap-4", className)}>
          <RedactedBanner
            fields={redactedFields}
            count={redactedCount}
            message={redactedMessage}
          />
          {children}
        </div>
      );

    case "normal":
    default:
      return <div className={className}>{children}</div>;
  }
}

/* ------------------------------------------------------- projection banners */

/**
 * Stale watermark: the source is older than the freshness budget. Shows when the
 * data was generated and offers a Refresh, without hiding the stale content.
 */
export function StaleBanner({
  asOf,
  message,
  onRefresh,
  className,
}: {
  asOf?: string;
  message?: string;
  onRefresh?: () => void;
  className?: string;
}) {
  return (
    <Alert
      data-state="stale"
      className={cn(
        "flex items-start justify-between gap-3 border-warning/40 bg-warning/10",
        className,
      )}
    >
      <div>
        <AlertTitle className="flex items-center gap-2">
          <Icon name="events" aria-hidden />
          Showing stale data
        </AlertTitle>
        <AlertDescription className="text-warning-foreground/90">
          {message ??
            (asOf
              ? `These records were generated ${asOf} and may be out of date.`
              : "These records may be out of date.")}
        </AlertDescription>
      </div>
      {onRefresh ? (
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <Icon name="reopen" data-icon="inline-start" aria-hidden />
          Refresh
        </Button>
      ) : null}
    </Alert>
  );
}

/** A compact stale chip for inline use on a section header (vs. the banner). */
export function StaleBadge({ asOf }: { asOf?: string }) {
  return (
    <Badge data-state="stale" variant="warning">
      <Icon name="events" aria-hidden />
      {asOf ? `Stale · ${asOf}` : "Stale"}
    </Badge>
  );
}

/**
 * Overflow disclosure: more records exist than are rendered. The count is shown
 * so a truncated list never silently drops data (architecture, not polish).
 */
export function OverflowBanner({
  count,
  message,
  className,
}: {
  count?: number;
  message?: string;
  className?: string;
}) {
  return (
    <Alert data-state="overflow" className={cn("border-border", className)}>
      <AlertTitle className="flex items-center gap-2">
        <Icon name="more" aria-hidden />
        More records than shown
      </AlertTitle>
      <AlertDescription>
        {message ??
          (typeof count === "number"
            ? `${count} more record${count === 1 ? "" : "s"} are hidden or collapsed.`
            : "Some records are hidden or collapsed.")}
      </AlertDescription>
    </Alert>
  );
}

/** A compact overflow chip for inline use (e.g. on a section header). */
export function OverflowBadge({ count }: { count?: number }) {
  return (
    <Badge data-state="overflow" variant="outline">
      <Icon name="more" aria-hidden />
      {typeof count === "number" ? `+${count} more` : "More"}
    </Badge>
  );
}

/**
 * Redaction disclosure: fields the viewer is not authorized to see. The names
 * are shown only when policy allows existence disclosure; otherwise a count.
 * Never leaks the withheld values themselves.
 */
export function RedactedBanner({
  fields,
  count,
  message,
  className,
}: {
  fields?: readonly string[];
  count?: number;
  message?: string;
  className?: string;
}) {
  const n = count ?? fields?.length;
  return (
    <Alert data-state="redacted" className={cn("border-border", className)}>
      <AlertTitle className="flex items-center gap-2">
        <Icon name="roles" aria-hidden />
        Some content is redacted
      </AlertTitle>
      <AlertDescription>
        {message ??
          (typeof n === "number"
            ? `${n} field${n === 1 ? "" : "s"} are withheld by access policy.`
            : "Some fields are withheld by access policy.")}
        {fields && fields.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {fields.map((field) => (
              <RedactedBadge key={field} label={field} />
            ))}
          </div>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/** A single "redacted: <field>" chip — the value is never rendered. */
export function RedactedBadge({ label }: { label?: string }) {
  return (
    <Badge data-state="redacted" variant="outline">
      <Icon name="roles" aria-hidden />
      {label ? `Redacted · ${label}` : "Redacted"}
    </Badge>
  );
}

/**
 * Forbidden downgrade: an action the viewer cannot take. Shown in place of the
 * control with the policy reason and the approval/escalation path — never a
 * silent missing button.
 */
export function ForbiddenNotice({
  reason,
  approvalPath,
  className,
}: {
  reason?: string;
  approvalPath?: string;
  className?: string;
}) {
  return (
    <Alert
      data-state="forbidden"
      variant="destructive"
      className={cn(className)}
    >
      <AlertTitle className="flex items-center gap-2">
        <Icon name="reject" aria-hidden />
        Not permitted
      </AlertTitle>
      <AlertDescription>
        {reason ?? "You do not have permission to perform this action."}
        {approvalPath ? (
          <p className="mt-1 text-muted-foreground">Request access via {approvalPath}.</p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Map a state to its semantic icon (used by tests and any state legend). Kept in
 * one place so the eight states stay visually consistent across screens.
 */
export const RENDER_STATE_ICON: Record<RenderStateKind, IconName> = {
  loading: "events",
  empty: "scope",
  normal: "resultOk",
  stale: "events",
  overflow: "more",
  redacted: "roles",
  forbidden: "reject",
  error: "reject",
};

export default RenderState;
